import React, { useEffect, useState } from 'react';
import { useUsersContext } from '../../contexts/UsersContext';
import UserDetails from '../../components/UsersDetails';
import UserForm from '../../components/UserForm';
import AdminMarketplace from '../../components/AdminMarketplace';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../../styles/Auth.css';
import '../../styles/Dashboard.css';
import '../../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useOutletContext();
  const { users, dispatch } = useUsersContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [customerCareData, setCustomerCareData] = useState({
    name: '',
    userId: '',
    phone: '+1234567890',
    password: ''
  });
  const [addingCustomerCare, setAddingCustomerCare] = useState(false);
  const [editRequests, setEditRequests] = useState([]);
  const [loadingEditRequests, setLoadingEditRequests] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, approved, rejected
  const [processingRequest, setProcessingRequest] = useState(null); // requestId being processed
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  const [userListView, setUserListView] = useState(() => {
    const saved = localStorage.getItem('admin-user-view');
    return saved || 'grid'; // 'grid' or 'list'
  });
  const [analyticsData, setAnalyticsData] = useState({
    users: null,
    sales: null,
    marketplace: null,
    loading: true
  });
  const navigate = useNavigate();

  // Dark mode toggle
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('admin-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Save view preference
  useEffect(() => {
    localStorage.setItem('admin-user-view', userListView);
  }, [userListView]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  // Calculate user counts by role
  const userCounts = users?.reduce((acc, user) => {
    acc[user.role] = (acc[user.role] || 0) + 1;
    return acc;
  }, {});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/admin/users');
        if (response.data.success) {
          dispatch({ type: 'SET_USERS', payload: response.data.data.users });
        } else {
          setError(response.data.message || 'Failed to fetch users');
        }
      } catch (err) {
        setError('Error fetching users');
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [dispatch]);

  // Fetch edit requests for customer care tab
  useEffect(() => {
    const fetchEditRequests = async () => {
      if (activeTab === 'customer-care') {
        setLoadingEditRequests(true);
        try {
          console.log('Fetching edit requests for admin...');
          const response = await api.get('/customer-care/edit-requests');
          console.log('Edit requests response:', response.data);
          if (response.data.success) {
            console.log('Edit requests data:', response.data.data);
            setEditRequests(response.data.data || []);
          } else {
            console.error('Failed to fetch edit requests:', response.data.message);
            setEditRequests([]);
          }
        } catch (err) {
          console.error('Error fetching edit requests:', err);
          console.error('Error details:', err.response?.data || err.message);
          setEditRequests([]);
        } finally {
          setLoadingEditRequests(false);
        }
      } else {
        // Clear edit requests when switching away from customer care tab
        setEditRequests([]);
      }
    };
    fetchEditRequests();
  }, [activeTab]);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (activeTab === 'analytics') {
        setAnalyticsData(prev => ({ ...prev, loading: true }));
        try {
          // Fetch user statistics
          const usersResponse = await api.get('/admin/users');
          const usersData = usersResponse.data.success ? usersResponse.data.data.users : [];
          
          // Fetch marketplace statistics
          let marketplaceData = null;
          try {
            const marketplaceResponse = await api.get('/admin/marketplace/stats');
            if (marketplaceResponse.data.success) {
              marketplaceData = marketplaceResponse.data.data;
            }
          } catch (err) {
            console.error('Error fetching marketplace stats:', err);
          }

          // Process user data for charts
          const userRoleDistribution = usersData.reduce((acc, user) => {
            acc[user.role] = (acc[user.role] || 0) + 1;
            return acc;
          }, {});

          // Calculate user growth (last 30 days)
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          const recentUsers = usersData.filter(user => new Date(user.createdAt || user.created_at) >= thirtyDaysAgo);
          
          setAnalyticsData({
            users: {
              total: usersData.length,
              byRole: userRoleDistribution,
              recent: recentUsers.length
            },
            sales: marketplaceData ? {
              totalRevenue: marketplaceData.revenue?.total || 0,
              totalOrders: marketplaceData.orders?.total || 0,
              pendingOrders: marketplaceData.orders?.pending || 0
            } : null,
            marketplace: marketplaceData,
            loading: false
          });
        } catch (err) {
          console.error('Error fetching analytics:', err);
          setAnalyticsData(prev => ({ ...prev, loading: false }));
        }
      }
    };
    fetchAnalytics();
  }, [activeTab]);

  // Handle approve edit request
  const handleApproveRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this request?')) {
      return;
    }
    
    setProcessingRequest(requestId);
    try {
      const response = await api.put(`/customer-care/edit-requests/${requestId}/approve`, {
        adminNotes: ''
      });
      
      if (response.data.success) {
        // Refresh the list
        const refreshResponse = await api.get('/customer-care/edit-requests');
        if (refreshResponse.data.success) {
          setEditRequests(refreshResponse.data.data || []);
        }
        alert('Request approved successfully!');
      } else {
        alert('Failed to approve request: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error approving request:', err);
      alert('Error approving request: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessingRequest(null);
    }
  };

  // Handle reject edit request
  const handleRejectRequest = async (requestId) => {
    const adminNotes = window.prompt('Please provide a reason for rejection:');
    if (adminNotes === null) {
      return; // User cancelled
    }
    
    setProcessingRequest(requestId);
    try {
      const response = await api.put(`/customer-care/edit-requests/${requestId}/reject`, {
        adminNotes: adminNotes || 'No reason provided'
      });
      
      if (response.data.success) {
        // Refresh the list
        const refreshResponse = await api.get('/customer-care/edit-requests');
        if (refreshResponse.data.success) {
          setEditRequests(refreshResponse.data.data || []);
        }
        alert('Request rejected successfully!');
      } else {
        alert('Failed to reject request: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      alert('Error rejecting request: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessingRequest(null);
    }
  };

  // Filter edit requests by status
  const filteredEditRequests = editRequests.filter(request => {
    if (filterStatus === 'all') return true;
    return request.status === filterStatus;
  });

  // Get request type display name
  const getRequestTypeDisplay = (type) => {
    const types = {
      'profile_update': 'Profile Update',
      'password_reset': 'Password Reset',
      'role_change': 'Role Change',
      'verification': 'Account Verification',
      'other': 'Other'
    };
    return types[type] || type;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter users based on search term, role, and status
  useEffect(() => {
    if (users) {
      const filtered = users.filter(user => {
        const matchesSearch =
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.role.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === 'All' || user.role === roleFilter;
        const matchesStatus =
          statusFilter === 'All' ||
          (statusFilter === 'Verified' && user.isVerified) ||
          (statusFilter === 'Unverified' && !user.isVerified);
        return matchesSearch && matchesRole && matchesStatus;
      });
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users, roleFilter, statusFilter]);

  const handleLogout = () => {
    if (typeof logout === 'function') {
      logout();
    } else {
      navigate('/login');
    }
  };

  const handleAddUser = async (userData) => {
    console.log('=== handleAddUser called ===');
    console.log('User data being sent:', userData);
    
    setAddingUser(true);
    try {
      const response = await api.post('/admin/users', userData);
      console.log('Response from server:', response.data);
      
      if (response.data.success) {
        // Add the new user to the users list
        dispatch({ type: 'ADD_USER', payload: response.data.data.user });
        setShowAddUserModal(false);
        setError(null);
        // Show success message
        alert('User created successfully!');
      } else {
        setError(response.data.message || 'Failed to create user');
      }
    } catch (err) {
      console.error('Error in handleAddUser:', err);
      console.error('Error response:', err.response?.data);
      setError('Error creating user: ' + (err.response?.data?.message || err.message));
    } finally {
      setAddingUser(false);
    }
  };

  const handleAddCustomerCareOfficer = async (e) => {
    e.preventDefault();
    setAddingCustomerCare(true);
    try {
      // Create a user with the customer care role using userId as email
      const userData = {
        name: customerCareData.name,
        email: `${customerCareData.userId}@weheal.local`, // Create a local email for login
        password: customerCareData.password,
        phone: customerCareData.phone,
        role: 'CustomerCare',
        isVerified: true
      };

      const response = await api.post('/admin/users', userData);
      if (response.data.success) {
        // Add the new user to the users list
        dispatch({ type: 'ADD_USER', payload: response.data.data.user });
        setCustomerCareData({ name: '', userId: '', phone: '+1234567890', password: '' });
        setError(null);
        // Show success message
        alert('Customer Care Officer created successfully!');
      } else {
        setError(response.data.message || 'Failed to create customer care officer');
      }
    } catch (err) {
      setError('Error creating customer care officer: ' + (err.response?.data?.message || err.message));
    } finally {
      setAddingCustomerCare(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setDeletingId(userId);
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      if (response.data.success) {
        // Update the users list by removing the deleted user
        dispatch({ type: 'DELETE_USER', payload: userId });
        // Show success message
        setError(null);
      } else {
        setError(response.data.message || 'Failed to delete user');
      }
    } catch (err) {
      setError('Error deleting user');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Prepare chart data
  const userRoleChartData = analyticsData.users?.byRole ? Object.entries(analyticsData.users.byRole).map(([role, count]) => ({
    name: role,
    value: count
  })) : [];

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-container">
        <div className="admin-dashboard-header">
          <h2>Admin Dashboard</h2>
          <div className="admin-header-actions">
            <button
              className="admin-dark-mode-toggle"
              onClick={toggleDarkMode}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <button className="admin-btn admin-btn-primary" onClick={handleLogout}>Logout</button>
          </div>
        </div>
        
        {error && (
          <div className="admin-error-message">
            {error}
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="admin-tabs">
          <div className="admin-tabs-list">
            {[
              { id: 'users', label: 'User Management' },
              { id: 'marketplace', label: 'Marketplace' },
              { id: 'customer-care', label: 'Customer Care' },
              { id: 'analytics', label: 'Analytics' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`admin-tab-button ${activeTab === tab.id ? 'active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <>
            {/* User Statistics Cards - modern design */}
            <div className="admin-stats-grid">
              {/* Total Users Card */}
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Total Users</p>
                <h3 className="admin-stat-card-value">{users?.length || 0}</h3>
                <div className="admin-stat-card-footer">
                  <strong style={{ color: 'var(--admin-primary)' }}>+{userCounts?.Patient || 0}</strong> patients
                </div>
              </div>

              {/* Doctors Card */}
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Doctors</p>
                <h3 className="admin-stat-card-value">{userCounts?.Doctor || 0}</h3>
                <div className="admin-stat-card-footer">
                  <strong style={{ color: 'var(--admin-primary)' }}>Active</strong> professionals
                </div>
              </div>

              {/* Nurses Card */}
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Nurses</p>
                <h3 className="admin-stat-card-value">{userCounts?.Nurse || 0}</h3>
                <div className="admin-stat-card-footer">
                  <strong style={{ color: 'var(--admin-primary)' }}>Care</strong> specialists
                </div>
              </div>
              {/* Patients Card */}
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Patients</p>
                <h3 className="admin-stat-card-value">{userCounts?.Patient || 0}</h3>
                <div className="admin-stat-card-footer">
                  <strong style={{ color: 'var(--admin-primary)' }}>Active</strong> accounts
                </div>
              </div>

              {/* Clinic Staff Card */}
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Clinic Staff</p>
                <h3 className="admin-stat-card-value">{userCounts?.ClinicStaff || 0}</h3>
                <div className="admin-stat-card-footer">
                  <strong style={{ color: 'var(--admin-primary)' }}>Support</strong> staff
                </div>
              </div>

              {/* Customer Care Card */}
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Customer Care</p>
                <h3 className="admin-stat-card-value">{userCounts?.CustomerCare || 0}</h3>
                <div className="admin-stat-card-footer">
                  <strong style={{ color: 'var(--admin-primary)' }}>Support</strong> team
                </div>
              </div>
            </div>
            
            {/* Users Section - Modern Redesign */}
            <div className="admin-card">
              <div className="admin-card-header">
                <div>
                  <h3 className="admin-card-title">All Users</h3>
                  <p style={{ margin: '0.5rem 0 0 0', color: 'var(--admin-text-secondary)', fontSize: '0.875rem' }}>
                    Manage and view all registered users ({filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'})
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  {/* View Toggle */}
                  <div className="admin-view-toggle">
                <button 
                      onClick={() => setUserListView('grid')}
                      className={`admin-view-toggle-btn ${userListView === 'grid' ? 'active' : ''}`}
                      title="Grid View"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setUserListView('list')}
                      className={`admin-view-toggle-btn ${userListView === 'list' ? 'active' : ''}`}
                      title="List View"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                  </div>
                  <button 
                    className="admin-btn admin-btn-primary" 
                  onClick={() => setShowAddUserModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add User
                </button>
                </div>
              </div>
              
              {/* Enhanced Search and Filters */}
              <div className="admin-users-filters">
                <div className="admin-search-wrapper">
                  <svg className="admin-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                <input
                  type="text"
                    placeholder="Search by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="admin-search-clear"
                      title="Clear search"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                <div className="admin-filters-group">
                  <select 
                    value={roleFilter} 
                    onChange={e => setRoleFilter(e.target.value)} 
                    className="admin-filter-select"
                  >
                  <option value="All">All Roles</option>
                  <option value="Admin">Admin</option>
                  <option value="Doctor">Doctor</option>
                  <option value="Nurse">Nurse</option>
                  <option value="Patient">Patient</option>
                    <option value="ClinicStaff">Clinic Staff</option>
                  <option value="CustomerCare">Customer Care</option>
                </select>
                  
                  <select 
                    value={statusFilter} 
                    onChange={e => setStatusFilter(e.target.value)} 
                    className="admin-filter-select"
                  >
                  <option value="All">All Status</option>
                  <option value="Verified">Verified</option>
                  <option value="Unverified">Unverified</option>
                </select>
                </div>
              </div>
              
              {/* User List - Grid or List View */}
              {filteredUsers.length === 0 ? (
                <div className="admin-empty-state">
                  <svg className="admin-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <h4>No users found</h4>
                  <p>Try adjusting your search or filters</p>
                </div>
              ) : userListView === 'grid' ? (
                <div className="admin-users-grid">
                  {filteredUsers.map((user) => (
                    <div key={user._id} className="admin-user-card">
                      <div className="admin-user-card-header">
                        <div className="admin-user-avatar-large">
                          {user.profilePicture ? (
                            <img src={user.profilePicture} alt={user.name} />
                          ) : (
                            <span>{user.name.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="admin-user-status-indicator" data-status={user.isVerified ? 'verified' : 'unverified'}>
                          <div className="admin-user-status-dot"></div>
                        </div>
                      </div>
                      
                      <div className="admin-user-card-body">
                        <h4 className="admin-user-name">{user.name}</h4>
                        <p className="admin-user-email">{user.email}</p>
                        
                        <div className="admin-user-meta">
                          <span className={`admin-role-badge admin-role-${user.role.toLowerCase()}`}>
                            {user.role === 'ClinicStaff' ? 'Clinic Staff' : user.role === 'CustomerCare' ? 'Customer Care' : user.role}
                          </span>
                          <span className={`admin-status-badge admin-status-${user.isVerified ? 'verified' : 'unverified'}`}>
                            {user.isVerified ? (
                              <>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Verified
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                Unverified
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      
                      <div className="admin-user-card-actions">
                        <button
                          className="admin-btn admin-btn-secondary admin-btn-sm"
                          onClick={() => navigate(`/dashboard/admin/user/${user._id}`)}
                          style={{ flex: 1 }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                        <button 
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => handleDeleteUser(user._id)}
                          disabled={deletingId === user._id}
                          style={{ flex: 1 }}
                        >
                          {deletingId === user._id ? (
                            <>
                              <div className="admin-spinner-small"></div>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="admin-table-container">
                  <table className="admin-table">
                  <thead>
                    <tr>
                        <th>User</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                            <div className="admin-user-list-item">
                              <div className="admin-user-avatar-small">
                                {user.profilePicture ? (
                                  <img src={user.profilePicture} alt={user.name} />
                                ) : (
                                  <span>{user.name.charAt(0).toUpperCase()}</span>
                                )}
                            </div>
                              <div>
                                <div className="admin-user-name-inline">{user.name}</div>
                                <div className="admin-user-status-indicator-inline" data-status={user.isVerified ? 'verified' : 'unverified'}>
                                  <div className="admin-user-status-dot-small"></div>
                                  <span>{user.isVerified ? 'Verified' : 'Unverified'}</span>
                                </div>
                              </div>
                          </div>
                        </td>
                          <td>
                            <span className="admin-user-email-inline">{user.email}</span>
                          </td>
                          <td>
                            <span className={`admin-role-badge admin-role-${user.role.toLowerCase()}`}>
                              {user.role === 'ClinicStaff' ? 'Clinic Staff' : user.role === 'CustomerCare' ? 'Customer Care' : user.role}
                          </span>
                        </td>
                        <td>
                            <span className={`admin-status-badge admin-status-${user.isVerified ? 'verified' : 'unverified'}`}>
                              {user.isVerified ? (
                                <>
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Verified
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                  Unverified
                                </>
                              )}
                          </span>
                        </td>
                        <td>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                                className="admin-btn admin-btn-secondary admin-btn-sm"
                            onClick={() => navigate(`/dashboard/admin/user/${user._id}`)}
                          >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                          </button>
                          <button 
                                className="admin-btn admin-btn-danger admin-btn-sm"
                            onClick={() => handleDeleteUser(user._id)}
                            disabled={deletingId === user._id}
                          >
                                {deletingId === user._id ? (
                                  <>
                                    <div className="admin-spinner-small"></div>
                                    Deleting...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Delete
                                  </>
                                )}
                          </button>
                            </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'marketplace' && (
          <AdminMarketplace />
        )}

        {activeTab === 'analytics' && (
          <div className="admin-analytics-content">
            {analyticsData.loading ? (
              <div className="admin-loading">
                <div className="admin-spinner"></div>
                <p style={{ marginLeft: '1rem' }}>Loading analytics...</p>
              </div>
            ) : (
              <>
                {/* Overview Stats */}
                <div className="admin-stats-grid">
                  <div className="admin-stat-card">
                    <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <p className="admin-stat-card-label">Total Users</p>
                    <h3 className="admin-stat-card-value">{analyticsData.users?.total || 0}</h3>
                    <div className="admin-stat-card-footer">
                      <strong style={{ color: 'var(--admin-primary)' }}>+{analyticsData.users?.recent || 0}</strong> new in last 30 days
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="admin-stat-card-label">Total Revenue</p>
                    <h3 className="admin-stat-card-value">৳{analyticsData.sales?.totalRevenue?.toLocaleString() || '0'}</h3>
                    <div className="admin-stat-card-footer">
                      From marketplace sales
                    </div>
                  </div>

                  <div className="admin-stat-card">
                    <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <p className="admin-stat-card-label">Total Orders</p>
                    <h3 className="admin-stat-card-value">{analyticsData.sales?.totalOrders || 0}</h3>
                    <div className="admin-stat-card-footer">
                      <strong style={{ color: 'var(--admin-warning)' }}>{analyticsData.sales?.pendingOrders || 0}</strong> pending
                    </div>
                  </div>

                  {analyticsData.marketplace && (
                    <>
                      <div className="admin-stat-card">
                        <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                        </div>
                        <p className="admin-stat-card-label">Total Products</p>
                        <h3 className="admin-stat-card-value">{analyticsData.marketplace.products?.total || 0}</h3>
                        <div className="admin-stat-card-footer">
                          <strong style={{ color: 'var(--admin-success)' }}>{analyticsData.marketplace.products?.active || 0}</strong> active
                        </div>
                      </div>

                      <div className="admin-stat-card">
                        <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="admin-stat-card-label">Medicines</p>
                        <h3 className="admin-stat-card-value">{analyticsData.marketplace.products?.medicines || 0}</h3>
                        <div className="admin-stat-card-footer">
                          Available in marketplace
                        </div>
                      </div>

                      <div className="admin-stat-card">
                        <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <p className="admin-stat-card-label">Lab Tests</p>
                        <h3 className="admin-stat-card-value">{analyticsData.marketplace.products?.labTests || 0}</h3>
                        <div className="admin-stat-card-footer">
                          Available in marketplace
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Charts */}
                <div className="admin-charts-grid">
                  {/* User Role Distribution Pie Chart */}
                  <div className="admin-chart-container">
                    <h3 className="admin-chart-title">User Distribution by Role</h3>
                    <div className="admin-chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={userRoleChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {userRoleChartData.map((entry, index) => {
                              const navyBlueColors = ['#1E40AF', '#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE'];
                              return <Cell key={`cell-${index}`} fill={navyBlueColors[index % navyBlueColors.length]} />;
                            })}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* User Role Distribution Bar Chart */}
                  <div className="admin-chart-container">
                    <h3 className="admin-chart-title">Users by Role</h3>
                    <div className="admin-chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={userRoleChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border-color)" />
                          <XAxis dataKey="name" stroke="var(--admin-text-secondary)" />
                          <YAxis stroke="var(--admin-text-secondary)" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'var(--admin-bg-card)', 
                              border: '1px solid var(--admin-border-color)',
                              color: 'var(--admin-text-primary)'
                            }}
                          />
                          <Bar dataKey="value" fill="#1E40AF" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Additional Analytics Info */}
                <div className="admin-card">
                  <h3 className="admin-card-title">Analytics Summary</h3>
                  <div className="admin-analytics-summary-grid">
                    <div>
                      <p style={{ color: 'var(--admin-text-secondary)', marginBottom: '0.5rem' }}>User Growth Rate</p>
                      <p style={{ color: 'var(--admin-text-primary)', fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                        {analyticsData.users?.total > 0 
                          ? ((analyticsData.users.recent / analyticsData.users.total) * 100).toFixed(1) 
                          : '0'}%
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--admin-text-secondary)', marginBottom: '0.5rem' }}>Average Revenue per Order</p>
                      <p style={{ color: 'var(--admin-text-primary)', fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                        ৳{analyticsData.sales?.totalOrders > 0 
                          ? (analyticsData.sales.totalRevenue / analyticsData.sales.totalOrders).toFixed(2) 
                          : '0.00'}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--admin-text-secondary)', marginBottom: '0.5rem' }}>Order Completion Rate</p>
                      <p style={{ color: 'var(--admin-text-primary)', fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                        {analyticsData.sales?.totalOrders > 0 
                          ? (((analyticsData.sales.totalOrders - analyticsData.sales.pendingOrders) / analyticsData.sales.totalOrders) * 100).toFixed(1) 
                          : '0'}%
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'customer-care' && (
          <>
            {/* Customer Care Statistics */}
            <div className="admin-stats-grid">
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
              </div>
                <p className="admin-stat-card-label">Total Requests</p>
                <h3 className="admin-stat-card-value">{editRequests.length}</h3>
                <div className="admin-stat-card-footer">
                  All edit requests
              </div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
              </div>
                <p className="admin-stat-card-label">Pending</p>
                <h3 className="admin-stat-card-value">{editRequests.filter(r => r.status === 'pending').length}</h3>
                <div className="admin-stat-card-footer">
                  Awaiting review
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Approved</p>
                <h3 className="admin-stat-card-value">{editRequests.filter(r => r.status === 'approved').length}</h3>
                <div className="admin-stat-card-footer">
                  Successfully processed
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Rejected</p>
                <h3 className="admin-stat-card-value">{editRequests.filter(r => r.status === 'rejected').length}</h3>
                <div className="admin-stat-card-footer">
                  Declined requests
                </div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-card-icon" style={{ background: 'linear-gradient(135deg, #1E40AF, #2563EB)' }}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="admin-stat-card-label">Officers</p>
                <h3 className="admin-stat-card-value">{userCounts?.CustomerCare || 0}</h3>
                <div className="admin-stat-card-footer">
                  Active customer care team
                </div>
              </div>
            </div>

            {/* Edit Requests Section - Full Width */}
            <div className="admin-card">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div>
                  <h3 className="admin-card-title">
                    Edit Requests
                  </h3>
                  <p style={{ margin: '0.5rem 0 0 0', color: 'var(--admin-text-secondary)', fontSize: '0.875rem' }}>
                    Review and manage user information change requests from customer care officers
                  </p>
                </div>
                
                {/* Status Filter */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {['all', 'pending', 'approved', 'rejected'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setFilterStatus(status)}
                      className={`admin-btn ${filterStatus === status ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
                      style={{
                        fontSize: '0.875rem',
                        textTransform: 'capitalize'
                      }}
                    >
                      {status} ({status === 'all' ? editRequests.length : editRequests.filter(r => r.status === status).length})
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="edit-requests-list">
                {loadingEditRequests ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto 1rem', width: '40px', height: '40px', border: '4px solid var(--admin-bg-secondary)', borderTop: '4px solid var(--admin-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <p style={{ fontSize: '0.875rem' }}>Loading edit requests...</p>
                  </div>
                ) : filteredEditRequests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--admin-text-secondary)' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📝</div>
                    <h4 style={{ marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600', color: 'var(--admin-text-primary)' }}>
                      {filterStatus === 'all' ? 'No Edit Requests' : `No ${filterStatus} Requests`}
                    </h4>
                    <p style={{ fontSize: '0.875rem', maxWidth: '400px', margin: '0 auto' }}>
                      {filterStatus === 'all' 
                        ? 'Edit requests from customer care officers will appear here once submitted.'
                        : `There are no ${filterStatus} edit requests at the moment.`}
                    </p>
                  </div>
                ) : (
                  <div className="requests-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {filteredEditRequests.map((request) => (
                      <div 
                        key={request._id} 
                        className="request-item" 
                        style={{ 
                          padding: '1.5rem', 
                          border: '2px solid',
                          borderColor: request.status === 'pending' ? 'var(--admin-warning)' : 
                                      request.status === 'approved' ? 'var(--admin-success)' : 'var(--admin-error)',
                          borderRadius: '12px', 
                          background: request.status === 'pending' ? '#fffbeb' : 
                                    request.status === 'approved' ? '#f0fdf4' : '#fef2f2',
                          transition: 'all 0.2s',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Status Badge */}
                        <div style={{ 
                          position: 'absolute', 
                          top: '0', 
                          right: '0', 
                          padding: '0.5rem 1rem',
                          background: request.status === 'pending' ? '#f59e0b' : 
                                    request.status === 'approved' ? '#10b981' : '#ef4444',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          borderBottomLeftRadius: '8px',
                          letterSpacing: '0.5px'
                        }}>
                          {request.status}
                        </div>

                        {/* Request Header - Compact */}
                        <div style={{ marginBottom: '1rem', paddingRight: '90px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <div style={{ 
                              width: '36px', 
                              height: '36px', 
                              borderRadius: '50%', 
                              background: 'linear-gradient(135deg, #1E40AF, #2563EB)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: '700',
                              fontSize: '0.875rem',
                              flexShrink: 0
                            }}>
                              {request.userName?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h4 style={{ margin: 0, fontSize: '0.9375rem', fontWeight: '600', color: 'var(--admin-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {request.userName || 'Unknown User'}
                              </h4>
                              <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                ID: {request.requestId}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Request Details Grid - Compact */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                          gap: '0.75rem',
                          marginBottom: '1rem',
                          fontSize: '0.8125rem'
                        }}>
                          <div>
                            <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Type
                            </p>
                            <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.8125rem', color: 'var(--admin-text-primary)', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {getRequestTypeDisplay(request.requestType)}
                            </p>
                          </div>
                          <div>
                            <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Officer
                            </p>
                            <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.8125rem', color: 'var(--admin-text-primary)', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {request.customerCareOfficerName || 'Unknown'}
                            </p>
                          </div>
                          <div>
                            <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Submitted
                            </p>
                            <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.8125rem', color: 'var(--admin-text-primary)', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {formatDate(request.createdAt)}
                            </p>
                          </div>
                          {request.processedAt && (
                            <div>
                              <p style={{ margin: 0, fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Processed
                              </p>
                              <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.8125rem', color: 'var(--admin-text-primary)', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {formatDate(request.processedAt)}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Current Data */}
                        {request.currentData && (
                          <div style={{ 
                            marginBottom: '0.875rem', 
                            padding: '0.625rem', 
                            background: 'var(--admin-bg-secondary)', 
                            borderRadius: '6px',
                            border: '1px solid var(--admin-border-color)'
                          }}>
                            <p style={{ margin: '0 0 0.375rem 0', fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Current Data
                            </p>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--admin-text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.5', maxHeight: '100px', overflowY: 'auto' }}>
                              {request.currentData}
                            </p>
                          </div>
                        )}

                        {/* Requested Changes */}
                        <div style={{ 
                          marginBottom: '0.875rem', 
                          padding: '0.625rem', 
                          background: 'rgba(30, 64, 175, 0.05)', 
                          borderRadius: '6px',
                          border: '1px solid rgba(30, 64, 175, 0.2)'
                        }}>
                          <p style={{ margin: '0 0 0.375rem 0', fontSize: '0.6875rem', color: 'var(--admin-primary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Requested Changes
                          </p>
                          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--admin-text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.5', maxHeight: '120px', overflowY: 'auto' }}>
                            {request.requestedChanges}
                          </p>
                        </div>

                        {/* Reason */}
                        <div style={{ 
                          marginBottom: '0.875rem', 
                          padding: '0.625rem', 
                          background: 'rgba(245, 158, 11, 0.05)', 
                          borderRadius: '6px',
                          border: '1px solid rgba(245, 158, 11, 0.2)'
                        }}>
                          <p style={{ margin: '0 0 0.375rem 0', fontSize: '0.6875rem', color: 'var(--admin-warning)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Reason
                          </p>
                          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--admin-text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.5', maxHeight: '100px', overflowY: 'auto' }}>
                            {request.reason}
                          </p>
                        </div>

                        {/* Admin Notes */}
                        {request.adminNotes && (
                          <div style={{ 
                            marginBottom: '0.875rem', 
                            padding: '0.625rem', 
                            background: 'var(--admin-bg-secondary)', 
                            borderRadius: '6px',
                            border: '1px solid var(--admin-border-color)'
                          }}>
                            <p style={{ margin: '0 0 0.375rem 0', fontSize: '0.6875rem', color: 'var(--admin-text-secondary)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Admin Notes
                            </p>
                            <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--admin-text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.5', maxHeight: '100px', overflowY: 'auto' }}>
                              {request.adminNotes}
                            </p>
                            {request.adminName && (
                              <p style={{ margin: '0.375rem 0 0 0', fontSize: '0.6875rem', color: 'var(--admin-text-secondary)' }}>
                                — {request.adminName}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Action Buttons - Always visible, responsive */}
                        {request.status === 'pending' && (
                          <div style={{ 
                            display: 'flex', 
                            gap: '0.625rem', 
                            marginTop: '0.875rem',
                            paddingTop: '0.875rem',
                            borderTop: '2px solid var(--admin-border-color)',
                            flexWrap: 'wrap'
                          }}>
                            <button 
                              onClick={() => handleApproveRequest(request.requestId)}
                              disabled={processingRequest === request.requestId}
                              style={{ 
                                flex: '1 1 auto',
                                minWidth: '120px',
                                padding: '0.625rem 1rem', 
                                background: processingRequest === request.requestId ? '#9ca3af' : '#10b981', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '6px', 
                                fontSize: '0.8125rem',
                                fontWeight: '600',
                                cursor: processingRequest === request.requestId ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.375rem',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => {
                                if (processingRequest !== request.requestId) {
                                  e.target.style.background = '#059669';
                                  e.target.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (processingRequest !== request.requestId) {
                                  e.target.style.background = '#10b981';
                                  e.target.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              {processingRequest === request.requestId ? (
                                <>
                                  <div className="loading-spinner-small" style={{ width: '12px', height: '12px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }}></div>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  ✓ Approve
                                </>
                              )}
                            </button>
                            <button 
                              onClick={() => handleRejectRequest(request.requestId)}
                              disabled={processingRequest === request.requestId}
                              style={{ 
                                flex: '1 1 auto',
                                minWidth: '120px',
                                padding: '0.625rem 1rem', 
                                background: processingRequest === request.requestId ? '#9ca3af' : '#ef4444', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '6px', 
                                fontSize: '0.8125rem',
                                fontWeight: '600',
                                cursor: processingRequest === request.requestId ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.375rem',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => {
                                if (processingRequest !== request.requestId) {
                                  e.target.style.background = '#dc2626';
                                  e.target.style.transform = 'translateY(-1px)';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (processingRequest !== request.requestId) {
                                  e.target.style.background = '#ef4444';
                                  e.target.style.transform = 'translateY(0)';
                                }
                              }}
                            >
                              {processingRequest === request.requestId ? (
                                <>
                                  <div className="loading-spinner-small" style={{ width: '12px', height: '12px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }}></div>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  ✕ Reject
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              </div>

            {/* Two Column Layout for Customer Care Officer Creation */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '2rem' }}>

              {/* Customer Care Officer Creation Section */}
              <div className="admin-card">
                <div className="admin-card-header">
                  <div>
                    <h3 className="admin-card-title">Add Customer Care Officer</h3>
                    <p style={{ margin: '0.5rem 0 0 0', color: 'var(--admin-text-secondary)', fontSize: '0.875rem' }}>
                    Create new customer care officer accounts
                  </p>
                  </div>
                </div>
                
                <form onSubmit={handleAddCustomerCareOfficer}>
                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={customerCareData.name}
                      onChange={(e) => setCustomerCareData({...customerCareData, name: e.target.value})}
                      placeholder="Enter full name"
                      required
                      className="admin-form-input"
                    />
                  </div>

                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      User ID *
                    </label>
                    <input
                      type="text"
                      value={customerCareData.userId}
                      onChange={(e) => setCustomerCareData({...customerCareData, userId: e.target.value})}
                      placeholder="Enter easy to remember user ID"
                      required
                      className="admin-form-input"
                    />
                    <small style={{ color: 'var(--admin-text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                      This will be used for login (e.g., "agent1", "support_john")
                    </small>
                  </div>

                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={customerCareData.phone || '+1234567890'}
                      onChange={(e) => setCustomerCareData({...customerCareData, phone: e.target.value})}
                      placeholder="Enter phone number"
                      required
                      className="admin-form-input"
                    />
                  </div>

                  <div className="admin-form-group">
                    <label className="admin-form-label">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={customerCareData.password}
                      onChange={(e) => setCustomerCareData({...customerCareData, password: e.target.value})}
                      placeholder="Enter password"
                      required
                      className="admin-form-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={addingCustomerCare}
                    className="admin-btn admin-btn-primary"
                    style={{ width: '100%' }}
                  >
                    {addingCustomerCare ? 'Creating...' : 'Create Customer Care Officer'}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="modal-overlay" onClick={() => setShowAddUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <UserForm 
              onSubmit={handleAddUser}
              onCancel={() => setShowAddUserModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
